import { useCallback, useEffect, useState } from 'react';
import { api } from '../lib/api';
import { SpinnerIcon } from '../onboarding/icons';
import {
  useSyncList,
  SyncStateTabs,
  Pager,
  SyncStateBadge,
  ListStates,
  SortableTh,
} from './SyncStatusBrowser';
import { ViewHeader } from './DashboardLayout';
import { useConnection, stripePriceUrl, hubspotProductUrl } from './links';
import { ExtLink } from './ExtLink';
import type { ProductListEntry } from './types';

/**
 * Dashboard → Products (M10b + M24).
 *
 * M24: the sync unit is the Stripe Price (1 Price = 1 HubSpot Product). This
 * view lists all Stripe prices with their HubSpot link state. The HubSpot
 * Product ID is shown under the Stripe Price ID (same pattern as the
 * subscriptions table's Deal ID under the subscription ID).
 *
 * Per-row actions (price-level, M24):
 *   - Link:   pick an existing HubSpot Product → product_mappings written → synced
 *   - Create: create a new HubSpot Product from the Stripe price → synced
 *   - Ignore: stop surfacing this price (sync_state='ignored')
 *   - Re-enable: un-ignore an ignored price
 *
 * Reference: docs/features/product-catalog.md (Flow G, M24 1:1 model)
 *            src/do/handlers/adminPanel.ts (linkPrice/createPrice/ignorePrice)
 */
export function Products() {
  const list = useSyncList<ProductListEntry>('admin/products');
  const {
    data,
    loading,
    error,
    refetch,
    state,
    setState,
    sort,
    dir,
    toggleSort,
    setOffset,
  } = list;

  return (
    <>
      <ViewHeader
        title="Prices"
        subtitle="Stripe Price catalog and its HubSpot Product link state."
      />
      <SyncStateTabs state={state} setState={setState} />

      {(loading || error || (data && data.items.length === 0)) && (
        <ListStates
          loading={loading}
          error={error}
          empty={!!data && data.items.length === 0}
          onRetry={refetch}
        />
      )}

      {data && data.items.length > 0 && (
        <>
          <div className="overflow-hidden rounded-xl border border-ink-200 bg-white">
            <table className="w-full text-sm">
              <thead className="bg-ink-50 text-left text-xs uppercase tracking-wide text-ink-500">
                <tr>
                  <SortableTh
                    label="Stripe price"
                    sortKey="name"
                    activeSort={sort}
                    sortDir={dir}
                    onSort={toggleSort}
                  />
                  <th className="px-4 py-2 font-medium">Product</th>
                  <th className="px-4 py-2 font-medium">State</th>
                  <th className="px-4 py-2 text-right font-medium">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ink-100">
                {data.items.map((p) => (
                  <ProductRow key={p.stripePriceId} product={p} onChanged={refetch} />
                ))}
              </tbody>
            </table>
          </div>
          <Pager
            total={data.total}
            offset={data.offset}
            pageSize={data.limit}
            setOffset={setOffset}
          />
        </>
      )}
    </>
  );
}

function ProductRow({
  product,
  onChanged,
}: {
  product: ProductListEntry;
  onChanged: () => void;
}) {
  // Pending action state — shows a spinner / disables the row while in flight.
  const [busy, setBusy] = useState<string | null>(null);
  // The Link picker (loaded on demand when the admin clicks Link).
  const [linking, setLinking] = useState(false);
  const { portalId, stripeAccountId } = useConnection();
  const stripeUrl = stripePriceUrl(stripeAccountId, product.stripePriceId);
  const hsUrl = hubspotProductUrl(portalId, product.hubspotProductId ?? '');

  const run = useCallback(
    async (
      op: 'create' | 'ignore' | 're-enable',
      endpoint: string
    ) => {
      setBusy(op);
      try {
        await api.post(endpoint, { stripePriceId: product.stripePriceId });
        onChanged();
      } catch (err) {
        alert(
          `${op} failed: ${err instanceof Error ? err.message : String(err)}`
        );
      } finally {
        setBusy(null);
      }
    },
    [product.stripePriceId, onChanged]
  );

  return (
    <tr className="hover:bg-ink-50/50">
      <td className="px-4 py-2">
        {stripeUrl ? (
          <ExtLink href={stripeUrl} label={product.stripePriceId} title="View in Stripe" />
        ) : (
          <div className="font-mono text-xs text-ink-700">
            {product.stripePriceId}
          </div>
        )}
        {product.hubspotProductId ? (
          <div className="mt-0.5 font-mono text-xs text-ink-400">
            {hsUrl ? (
              <ExtLink href={hsUrl} label={product.hubspotProductId} title="HubSpot Product" />
            ) : (
              product.hubspotProductId
            )}
          </div>
        ) : (
          <div className="mt-0.5 text-xs text-ink-400">unlinked</div>
        )}
        {!product.active && (
          <span className="text-xs text-ink-400">archived in Stripe</span>
        )}
      </td>
      <td className="px-4 py-2">
        <div className="font-medium text-ink-800">{product.productName}</div>
        <div className="text-xs text-ink-500">
          {product.priceDisplay ?? '—'}
        </div>
      </td>
      <td className="px-4 py-2">
        <SyncStateBadge state={product.syncState} />
      </td>
      <td className="px-4 py-2 text-right">
        <div className="flex justify-end gap-1.5">
          {(product.syncState === 'pending' ||
            product.syncState === 'broken') && (
              <>
                <button
                  className="btn-ghost px-2 py-1 text-xs"
                  disabled={!!busy}
                  onClick={() => setLinking(true)}
                >
                  Link
                </button>
                <button
                  className="btn-ghost px-2 py-1 text-xs"
                  disabled={!!busy}
                  onClick={() => run('create', 'admin/price/create')}
                >
                  {busy === 'create' ? '…' : 'Create'}
                </button>
                <button
                  className="btn-ghost px-2 py-1 text-xs text-ink-400"
                  disabled={!!busy}
                  onClick={() => run('ignore', 'admin/price/ignore')}
                >
                  {busy === 'ignore' ? '…' : 'Ignore'}
                </button>
              </>
            )}
          {product.syncState === 'synced' && (
            <span className="text-xs text-ink-400">linked ✓</span>
          )}
          {product.syncState === 'ignored' && (
            <>
              <span className="text-xs text-ink-400">ignored</span>
              <button
                className="btn-ghost px-2 py-1 text-xs"
                disabled={!!busy}
                onClick={() =>
                  run('re-enable', 'admin/price/re-enable')
                }
              >
                {busy === 're-enable' ? '…' : 'Re-enable'}
              </button>
            </>
          )}
        </div>
      </td>
      {linking && (
        <LinkPicker
          stripePriceId={product.stripePriceId}
          onClose={() => setLinking(false)}
          onLinked={() => {
            setLinking(false);
            onChanged();
          }}
        />
      )}
    </tr>
  );
}

/**
 * Link picker modal — fetches the tenant's HubSpot Products and lets the admin
 * pick one to map the Stripe price to (M24: 1:1). Uses the existing
 * /api/admin/hubspot/products endpoint.
 */
function LinkPicker({
  stripePriceId,
  onClose,
  onLinked,
}: {
  stripePriceId: string;
  onClose: () => void;
  onLinked: () => void;
}) {
  const [hsProducts, setHsProducts] = useState<
    { id: string; name: string; }[]
  >([]);
  const [loading, setLoading] = useState(true);
  const [picked, setPicked] = useState<string>('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api
      .get<{ items: { id: string; name: string; }[]; }>(
        'admin/hubspot/products'
      )
      .then((r) => setHsProducts(r.items ?? []))
      .catch(() => setHsProducts([]))
      .finally(() => setLoading(false));
  }, []);

  const link = async () => {
    if (!picked) return;
    setBusy(true);
    try {
      await api.post('admin/price/link', {
        stripePriceId,
        hubspotProductId: picked,
      });
      onLinked();
    } catch (err) {
      alert(`Link failed: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink-900/40">
      <div className="card w-full max-w-md p-6">
        <h3 className="mb-3 text-sm font-semibold text-ink-800">
          Link to HubSpot Product
        </h3>
        {loading ? (
          <p className="flex items-center gap-2 text-sm text-ink-400">
            <SpinnerIcon className="h-4 w-4" /> Loading HubSpot products…
          </p>
        ) : hsProducts.length === 0 ? (
          <p className="text-sm text-ink-500">
            No HubSpot Products found. Use Create instead.
          </p>
        ) : (
          <select
            className="input"
            value={picked}
            onChange={(e) => setPicked(e.target.value)}
            size={Math.min(8, hsProducts.length)}
          >
            <option value="">— pick a product —</option>
            {hsProducts.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name} ({p.id})
              </option>
            ))}
          </select>
        )}
        <div className="mt-4 flex justify-end gap-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn-primary"
            disabled={!picked || busy}
            onClick={link}
          >
            {busy ? 'Linking…' : 'Link'}
          </button>
        </div>
      </div>
    </div>
  );
}
